// deviceLocalSession — who is standing at this tablet, when there is no signal.
// Spec: docs/specs/device-and-worker-sign-in.md §5 (D11), §4.6. Phase 4f.
//
// ⚠️⚠️ A LOCAL SESSION IS NOT A SESSION. It grants nothing.
//
// The two must never be confused, so state the difference plainly:
//
//   A SUPABASE SESSION is authority. It is minted by the server after it has
//     judged a badge and a face, it carries claims RLS reads, and it is what
//     lets somebody see or change anything.
//
//   A LOCAL SESSION is attribution. It is this tablet's own note of who it
//     believes is standing in front of it, written after `deviceOfflineSignIn`
//     matched a cached face. It reads nothing, writes nothing, and unlocks no
//     screen that a signed-out device could not also reach. All it does is
//     supply `worker_number` and `identified_via` to captures that go into a
//     signed queue and are re-verified on drain (§5 rule 2).
//
// If a future edit makes a local session gate a read, restore a Supabase
// session, or satisfy a permission check, that is the bug. The whole reason
// `deviceOfflineSignIn` is allowed to judge a face at all is that its verdict
// buys only this.
//
// ⚠️ STORED IN PLAINTEXT, and that is not an oversight. It holds a worker
// number and a display name — both already on the encrypted roster, and both
// printed on the badge in the person's hand. Encrypting it would imply it is
// a credential. It is not one; there is nothing here to steal that a queue
// entry does not already carry in the clear.

import type { IdentifiedVia } from '@/lib/deviceCaptureQueue'

const KEY = 'kf49.device.local-session'

/** Absolute cap on a local session, whatever a profile says. */
export const MAX_LOCAL_SESSION_SECONDS = 12 * 60 * 60

/**
 * Fallback idle timeout when the profile has not been fetched.
 *
 * Short on purpose. An unknown timeout on a shared wall tablet must resolve
 * toward "lock sooner", because the failure it prevents is the next person
 * capturing work under the last person's name.
 */
export const DEFAULT_IDLE_SECONDS = 5 * 60

export interface LocalSession {
  worker_number:  string
  display_name:   string
  identified_via: IdentifiedVia
  /** ms epoch. */
  started_at:     number
  /** ms epoch of the last interaction — the idle clock restarts from here. */
  touched_at:     number
  /** True when the identification happened with no server involved. */
  offline:        boolean
  /** The worker's end date when it has passed (from the cached roster) —
   *  the capture screen's leaver line. Absent for everyone else. */
  end_date?:      string | null
}

function read(): LocalSession | null {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as LocalSession) : null
  } catch {
    return null
  }
}

function write(s: LocalSession): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)) } catch { /* best effort */ }
}

/** Start a local session for the worker this device believes it identified. */
export function beginLocalSession(input: {
  worker_number:  string
  display_name:   string
  identified_via: IdentifiedVia
  offline:        boolean
  end_date?:      string | null
  nowMs?:         number
}): LocalSession {
  const now = input.nowMs ?? Date.now()
  const s: LocalSession = {
    worker_number:  input.worker_number,
    display_name:   input.display_name,
    identified_via: input.identified_via,
    started_at:     now,
    touched_at:     now,
    offline:        input.offline,
    ...(input.end_date ? { end_date: input.end_date } : {}),
  }
  write(s)
  return s
}

/** Note an interaction, restarting the idle clock. */
export function touchLocalSession(nowMs: number = Date.now()): void {
  const s = read()
  if (!s) return
  write({ ...s, touched_at: nowMs })
}

export function endLocalSession(): void {
  try { localStorage.removeItem(KEY) } catch { /* best effort */ }
}

export type ExpiryReason = 'IDLE' | 'MAX_DURATION'

/**
 * Has this local session run out?
 *
 * ⚠️ TWO clocks, and both are needed. Idle catches the ordinary case — someone
 * walks away and the next person must not inherit their name. Max-duration
 * catches the one idle cannot: a tablet in a pocket, or a screen something
 * keeps touching, which would otherwise stay signed in as one picker for a week.
 */
export function localSessionExpiry(
  s: LocalSession | null,
  idleSeconds: number,
  nowMs: number = Date.now(),
): ExpiryReason | null {
  if (!s) return null

  // A clock that moved backwards (NTP correction, a tablet that lost its RTC)
  // must not read as "no time has passed" — the safe reading of a nonsense
  // timestamp is that the session is over.
  if (s.touched_at > nowMs || s.started_at > nowMs) return 'IDLE'

  const idleMs = Math.max(0, idleSeconds || DEFAULT_IDLE_SECONDS) * 1000
  if (nowMs - s.touched_at > idleMs) return 'IDLE'
  if (nowMs - s.started_at > MAX_LOCAL_SESSION_SECONDS * 1000) return 'MAX_DURATION'
  return null
}

/**
 * The live local session, or null once it has expired.
 *
 * ⚠️ Reading is what clears an expired one. There is no timer that must have
 * fired and no sweep to forget to run — a caller that asks who is signed in
 * gets the truth, including on a tablet that was asleep for the whole timeout.
 */
export function getLocalSession(
  idleSeconds: number,
  nowMs: number = Date.now(),
): LocalSession | null {
  const s = read()
  if (!s) return null
  if (localSessionExpiry(s, idleSeconds, nowMs)) {
    endLocalSession()
    return null
  }
  return s
}

/** Operator-facing sentence for why the screen locked itself. */
export function expiryMessage(reason: ExpiryReason): string {
  return reason === 'IDLE'
    ? 'Signed out — the tablet was left alone. Scan your badge to carry on.'
    : 'Signed out for the day. Scan your badge to carry on.'
}
